import { useEffect, useState } from 'react'
import { durationSince, laneWords, loadSupportQuestionOriginal, type SupportThreadHead } from '../lib/support'
import { useHubLang, dateLocaleFor } from '../lib/i18n'
import { HELP, SUPPORT } from '../lib/hubCopy'
import { PriorityPill } from './SupportInbox'
import { loadCommentOriginal, type WikiComment } from '../lib/wiki'
import { TranslatedLine, useOriginal } from './TranslatedLine'
import { signedSupportPictureUrl } from '../lib/supportMedia'
import { fillSavedReply, insertSavedReply, type SavedReply } from '../lib/supportReplies'
import { GuideLink } from './guides/GuideLink'

/**
 * One private thread — HLP-22, and the screen the bell finally has somewhere to
 * send people to.
 *
 * ── IT IS IN components/, NOT IN THE ROUTE FILE ─────────────────────────────
 *
 * Importing a route pulls in AppShell and then an SVG the test bundler cannot
 * resolve, so anything defined in `routes/` is unreachable by the web suite
 * with every assertion passing against an empty string. Everything here takes
 * its data as props for the same reason: an effect does not run under
 * `renderToString`.
 *
 * ── ONE COMPONENT FOR BOTH PARTIES ──────────────────────────────────────────
 *
 * The asker and the lane holder see the same thread, because it IS the same
 * thread — `app.content_is_visible` gives them both exactly this row and
 * `content_comment_read` gives them both exactly these replies. Two components
 * would be two answers to what a private question looks like, and the two would
 * drift (standing rule 11).
 *
 * What differs is ONE control, and the server decides which side you are on:
 * `iAsked` comes from `support_question()`, never from comparing ids here.
 *
 * ── ONE BOX, TWO DESTINATIONS (118) ─────────────────────────────────────────
 *
 * A lane holder can reply to the asker or leave a note the asker never sees
 * (117's `support_note`). Both were boxes; now they are one box with the
 * destination said in a sentence ABOVE it before anybody types, and the note
 * mode changes the box itself (dashed, grey) and the button's label. The risk
 * this carries is sending a note as a reply, which is why the destination is
 * a sentence and a visible change rather than a toggle nobody reads. The two
 * write paths are unchanged: `addComment` (035, rule 11) and
 * `add_support_note` (117). `onNote` is wired only for a holder of this lane;
 * the asker's box has one destination and no modes.
 *
 * ── A SAVED REPLY IS A STARTING POINT (122, HLP-30) ─────────────────────────
 *
 * A HOLDER of this lane (the route passes `savedReplies` for nobody else —
 * not the assignee, whose one thread is not the lane's wording) may pick one
 * of the lane's saved replies; it lands in the box, `{name}` filled with the
 * asker's first name, and is edited and sent like anything typed. The picker
 * is drawn only in REPLY mode: a note is what a colleague should know about
 * THIS thread, and a template for that is a template for not reading it.
 *
 * ── EACH SIDE READS IN ITS OWN LANGUAGE (123) ───────────────────────────────
 *
 * The question and every reply arrive already in the reader's language when
 * One has a rendering, with `translationStatus` saying so; the SERVER decided
 * which words to send. What this component adds is the one honest thing: the
 * teal "Translated by One · Show original" line ABOVE any words a machine
 * wrote — `TranslatedLine`, the same rendering the feed card and Wiki One use
 * (111, 112) — and the original fetched only on that press. A reply is its
 * own small component because each one needs its own press.
 */

export function SupportThread({
  head,
  replies,
  onReply,
  onNote,
  savedReplies = [],
  onVerdict,
  onBack,
  onClose,
  onReopen,
  onReplyAndClose,
  detailsOpen,
  onToggleDetails,
  busy = false,
  error,
}: {
  head: SupportThreadHead
  replies: WikiComment[]
  onReply?: (body: string) => void
  /** 118. A team note. Wired only for a holder of this question's lane; its
   *  presence is what renders the mode switch. */
  onNote?: (body: string) => void
  /** 122. The lane's saved replies. Passed only for a HOLDER of this lane;
   *  empty means no picker. */
  savedReplies?: SavedReply[]
  /** Only ever wired for the asker — see the block below. */
  onVerdict?: (answered: boolean) => void
  onBack?: () => void
  /** 174. The lane's close, its undo, and reply-then-close. Wired only for
   *  whoever STAFFS this thread (holder or assignee); the server refuses
   *  everybody else, and the asker's own word stays `onVerdict`. */
  onClose?: () => void
  onReopen?: () => void
  onReplyAndClose?: (body: string) => void
  /** 174. The Details tray (the staff panel) is toggled from the thread's
   *  head, where the hands already are. Absent = no button. */
  detailsOpen?: boolean
  onToggleDetails?: () => void
  busy?: boolean
  error?: string | null
}) {
  const lang = useHubLang()
  const t = SUPPORT[lang]
  const lane = laneWords(head.lane, lang)
  const [draft, setDraft] = useState('')
  const [mode, setMode] = useState<'reply' | 'note'>('reply')
  const noting = mode === 'note' && !!onNote
  const firstName = (head.askedBy ?? t.somebody).split(/\s+/)[0] ?? t.somebody
  /* 118. Whose turn, from the ONE home (app.support_turn()), worded for the
     side reading it. The asker's "they replied" is the lane's "waiting on
     them"; neither is derived here. */
  const turnWords = head.iAsked ? t.turn : t.turnHolder
  /* 123. The asker's own words behind a translated question, on the press. */
  const qOrig = useOriginal(head.translationStatus === 'machine', () => loadSupportQuestionOriginal(head.questionId))

  return (
    <div className="supportthread">
      {onBack && (
        <button type="button" className="btn btn--quiet btn--sm supportthread__back" onClick={onBack}>
          {/* Named, not "Back". A browser already has Back; what this offers is
              the list, which is the thing that makes a thread findable again
              tomorrow. */}
          {head.iAsked ? t.allYourQuestions : t.backToQueue}
        </button>
      )}

      <article className="card supportq">
        <header className="supportq__head">
          <span className="pill" title={lane.about}>
            {lane.label}
          </span>
          {/* PRIVATE MEANS PRIVATE TO A NAMED AUDIENCE, NOT ANONYMOUS (16 Sep).
              The asker is named to the receiving team and the ask surface says
              so before they type; hiding it here would claim an anonymity
              B15's k=6 says does not survive 57 business units anyway. */}
          <span className="supportq__who">{head.askedBy ?? t.somebody}</span>
          <time className="supportq__when" dateTime={head.askedAt}>
            {new Date(head.askedAt).toLocaleDateString(dateLocaleFor(lang) ?? 'en-CA')}
          </time>
          {head.turn && (
            <span
              className={`pill supportq__turn${
                (head.iAsked ? head.turn === 'asker' || head.turn === 'closed' : head.turn === 'holder') ? ' pill--live' : head.turn === 'answered' ? ' pill--kind' : head.turn === 'closed' ? ' supportq__turn--closed' : ''
              }`}
            >
              {turnWords[head.turn]}
              {!head.iAsked && head.turn === 'holder' && ` · ${durationSince(head.turnSince)}`}
            </span>
          )}
          {/* 348. The asker's priority, to both readers: the asker in their own
              words ("You said: Blocked"), the lane in the desk's. Recorded,
              never editable. */}
          {head.askerPriority &&
            (head.iAsked ? (
              <span className="pill">{HELP[lang].youSaid(HELP[lang].priorities[head.askerPriority].label)}</span>
            ) : (
              <PriorityPill priority={head.askerPriority} />
            ))}
          {/* 119. Who it is handed to — shown to BOTH readers. The asker was
              told which team would read this; a colleague outside that team
              now does, and silently widening a private question's audience is
              the one thing this must never do. */}
          {head.assignedTo && (
            <span className="pill pill--kind supportq__handed">
              {head.iAmAssignee ? t.handedToYou : t.handedTo(head.assignedTo)}
            </span>
          )}
          {/* 174. THE ONE OBVIOUS NEXT STEP, AT THE TOP. David could not find
              how to finish d249936b: there was no close, and the staff tools
              sat at the bottom of a tall column. Close (or Reopen) and the
              Details tray live in the thread's own head now. A confirmed
              question has no Close — the asker already finished it. */}
          {(onClose || onReopen || onToggleDetails) && (
            <span className="supportq__acts">
              {head.turn === 'closed' && onReopen && (
                <button type="button" className="btn btn--sm" disabled={busy} onClick={onReopen}>
                  {t.reopenThread}
                </button>
              )}
              {head.turn !== 'closed' && head.turn !== 'answered' && onClose && (
                <button type="button" className="btn btn--sm supportq__close" disabled={busy} onClick={onClose} title={`${t.closeThread} (e)`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="m5 12 5 5L20 7" /></svg>
                  {t.closeThread}
                </button>
              )}
              {/* 353 (HLP-50): the desk's guide, for staff only (onClose/onReopen are
                  passed to staff alone), and only if they may read it. */}
              {(onClose || onReopen) && <GuideLink guide="support-desk" section="close" label="How closing works" />}
              {onToggleDetails && (
                <button
                  type="button"
                  className="btn btn--sm btn--quiet"
                  aria-pressed={!!detailsOpen}
                  aria-controls="support-details"
                  onClick={onToggleDetails}
                >
                  {t.details}
                </button>
              )}
            </span>
          )}
        </header>

        {/* 123. Above the words, before anybody reads them (011's rule). */}
        <TranslatedLine state={qOrig} />
        <h3 className="supportq__q">{qOrig.showingOriginal && qOrig.original ? qOrig.original.title : head.question}</h3>
        {(qOrig.showingOriginal && qOrig.original ? qOrig.original.body : head.detail) && (
          <p className="supportq__detail">{qOrig.showingOriginal && qOrig.original ? qOrig.original.body : head.detail}</p>
        )}

        {/* ONE'S SUMMARY TRAVELS WITH THE VERBATIM QUESTION, NEVER INSTEAD OF
            IT (David, 17 Sep). Marked as the machine's words, and NOT teal:
            teal-and-the-mark marks a control that sends to the model and an
            answer coming back from it, and this is a quotation sitting inside
            somebody else's question. */}
        {head.oneSummary && (
          <details className="supportq__one">
            <summary>{t.whatOneSaid}</summary>
            <p>{head.oneSummary}</p>
          </details>
        )}

        {/* 116. The pictures and the page details, to the thread's two readers
            and nobody else (support_question() is INVOKER over 116's policies).
            The asker sees exactly what the team sees — they were told these
            would go, and this is what went. */}
        {head.pictures.length > 0 && (
          <div>
            <p className="supportq__picshead">{t.picturesHead(head.pictures.length)}</p>
            <ul className="supportq__pics">
              {head.pictures.map((p) => (
                <li key={p.path}>
                  <SupportPictureLink path={p.path} width={p.width} height={p.height} />
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* 118: OPEN, NOT FOLDED. For a platform holder the screen and the app
            version are the first thing checked on every ticket, and a closed
            details cost a click each time. The asker sees the same strip — 116
            sends it to both readers, and they were told these would go. */}
        {head.pageContext && Object.keys(head.pageContext).length > 0 && (
          <div className="supportq__ctx">
            <span className="supportq__ctxlbl">{t.pageDetailsHead}</span>
            {(['route', 'app_version', 'viewport', 'browser', 'locale'] as const).map((k) =>
              head.pageContext?.[k] ? (
                <span className="flag" key={k} title={`${t.pageDetailLabels[k]}: ${head.pageContext?.[k]}`}>
                  {k === 'browser' ? shortBrowser(String(head.pageContext?.[k])) : head.pageContext?.[k]}
                </span>
              ) : null,
            )}
          </div>
        )}
      </article>

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      <div className="supportq__thread">
        {replies.length === 0 ? (
          <p className="supportq__empty">
            {head.iAsked
              ? t.noReplyAsker
              : t.noReplyHolder}
          </p>
        ) : (
          replies.map((r) => (
            <SupportReply key={r.commentId} reply={r} fromAsker={r.authorPersonId === head.askedByPersonId} />
          ))
        )}

        {/* ── THE REPLY BOX, FOR EITHER PARTY ────────────────────────────────
            `addComment` and nothing else. There is no `answer_support_question`
            and no separate "asker message" path: `content_comment_read` IS
            089's read policy, so it already gives both of them the write and
            nobody else either, and 035 settled that replying to a post and
            replying to a question are the same ACT. A second mechanism would
            be a second answer to who may read this.

            118: for a lane holder the same box also takes a team note — see
            the header. The mode is stated in words above the box, and the
            box changes shape, before anything is typed. */}
        {/* 174. Said where the next reply goes, before anybody types. */}
        {head.turn === 'closed' && !head.iAsked && (onReply || onNote) && (
          <p className="supportq__closednote">{t.closedHolderNote}</p>
        )}
        {(onReply || onNote) && (
          <form
            className={`supportq__answer${noting ? ' supportq__answer--note' : ''}`}
            onSubmit={(e) => {
              e.preventDefault()
              if (!draft.trim()) return
              if (noting) onNote?.(draft.trim())
              else onReply?.(draft)
              setDraft('')
            }}
          >
            {onNote && onReply && (
              <div className="supportq__modes">
                <div className="chips" role="group" aria-label="Where this goes">
                  <button
                    type="button"
                    className={`chip chip--sm${!noting ? ' chip--on' : ''}`}
                    aria-pressed={!noting}
                    onClick={() => setMode('reply')}
                  >
                    {t.replyTo(firstName)}
                  </button>
                  <button
                    type="button"
                    className={`chip chip--sm${noting ? ' chip--on' : ''}`}
                    aria-pressed={noting}
                    onClick={() => setMode('note')}
                  >
                    {t.noteForTeam}
                  </button>
                </div>
                <p className="supportq__goesto">{noting ? t.noteGoesTo(firstName) : t.replyGoesTo(firstName)}</p>
              </div>
            )}
            {!noting && !head.iAsked && savedReplies.length > 0 && (
              <div className="supportq__saved">
                <label className="sr-only" htmlFor={`saved-${head.questionId}`}>
                  {t.insertSavedReply}
                </label>
                <select
                  id={`saved-${head.questionId}`}
                  className="supportstaff__select supportq__savedpick"
                  value=""
                  disabled={busy}
                  onChange={(e) => {
                    const r = savedReplies.find((x) => x.id === e.target.value)
                    if (!r) return
                    setDraft((cur) => insertSavedReply(cur, fillSavedReply(r.body, head.askedBy ? firstName : null)))
                  }}
                >
                  <option value="">{t.insertSavedReply}</option>
                  {savedReplies.map((r) => (
                    <option key={r.id} value={r.id}>{r.title}</option>
                  ))}
                </select>
              </div>
            )}
            {/* 353 (HLP-50): one link for the reply box - a reply, a note for
                the team, a saved reply - staff only, by the same test as Close. */}
            {!head.iAsked && (onClose || onReopen) && <GuideLink guide="support-desk" section="reply" label="How replies and notes work" />}
            <label className="sr-only" htmlFor={`reply-${head.questionId}`}>
              {noting ? t.noteForTeam : head.iAsked ? t.replyToTeam : t.replyToQuestion}
            </label>
            <textarea
              id={`reply-${head.questionId}`}
              className="composer__body"
              rows={3}
              maxLength={3000}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                noting
                  ? t.notePlaceholder
                  : head.iAsked
                    ? t.placeholderAsker
                    : t.placeholderHolder
              }
            />
            <div className="supportq__send">
              {/* 174. Reply, or reply AND close — the common end of a thread
                  ("fixed, try it now"). Close is a second act after the reply
                  (the server's close time is the later one), never instead of
                  it. Not offered in note mode: a note ends nothing. */}
              {!noting && onReplyAndClose && !head.iAsked && head.turn !== 'answered' ? (
                <span className="supportq__split">
                  <button className="btn btn--sm" type="submit" disabled={busy || !draft.trim()}>
                    {busy ? t.sending : t.reply}
                  </button>
                  <button
                    className="btn btn--sm btn--primary"
                    type="button"
                    disabled={busy || !draft.trim()}
                    onClick={() => {
                      if (!draft.trim()) return
                      onReplyAndClose(draft)
                      setDraft('')
                    }}
                  >
                    {t.replyAndClose}
                  </button>
                </span>
              ) : (
                <button
                  className={`btn btn--sm${noting ? '' : ' btn--primary'}`}
                  type="submit"
                  disabled={busy || !draft.trim()}
                >
                  {busy ? t.sending : noting ? t.addNote : t.reply}
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      {/* ── "DID THIS ANSWER YOUR QUESTION?" ───────────────────────────────────
          THE ASKER ONLY, and the server refuses everybody else including the
          lane holder (rule 21) — this condition is presentation on top of that,
          not the gate. A holder marking their own answer as resolving is how a
          queue looks clean while people are still stuck, and it would be
          invisible, because a resolved thread is one nobody looks at again.

          IT IS NOT A RATING OF THE REPLY (David, 18 Sep). It asks whether the
          QUESTION is resolved, which is why the state lives on the question and
          why nothing here can be grouped by who answered it — see 090's header
          and B22, B09 section 5, rule 8.

          AND "NO" MUST NOT READ AS A COMPLAINT. It leaves the thread open, it
          closes nothing, and it is the only evidence this queue can produce
          that an answer did not land — so the wording is about the QUESTION's
          state ("Not yet") rather than the answer's quality ("That didn't
          help"), and nothing here is styled as a failure. Somebody who
          hesitates to press it costs the queue the one signal it needs. */}
      {head.iAsked && onVerdict && (
        <div className={`card supportverdict${head.turn === 'closed' ? ' supportverdict--closed' : ''}`}>
          {/* 174. The lane closed it: the same two answers, asked as what they
              mean now. Yes confirms; No reopens it at the top of the lane's
              list (say_it_answered(false) after the close — the server derives
              the rest). Takes precedence over an older "not yet", which the
              close came after. */}
          {head.turn === 'closed' ? (
            <>
              <p className="supportverdict__q">{t.closedAsk}</p>
              <p className="supportverdict__lede">{t.closedAskLede}</p>
              <div className="supportverdict__actions">
                <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => onVerdict(true)}>
                  {t.yesSolved}
                </button>
                <button type="button" className="btn btn--sm" disabled={busy} onClick={() => onVerdict(false)}>
                  {t.noStillNeedHelp}
                </button>
              </div>
            </>
          ) : head.answeredTheAsker === null ? (
            <>
              <p className="supportverdict__q">{t.didItAnswer}</p>
              <div className="supportverdict__actions">
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={busy}
                  onClick={() => onVerdict(true)}
                >
                  {t.yesThatsIt}
                </button>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => onVerdict(false)}
                >
                  {t.notYet}
                </button>
              </div>
            </>
          ) : head.answeredTheAsker ? (
            <p className="supportverdict__done" aria-live="polite">
              {t.markedAnswered}{' '}
              <button
                type="button"
                className="btn btn--quiet btn--sm"
                disabled={busy}
                onClick={() => onVerdict(false)}
              >
                {t.actuallyNotYet}
              </button>
            </p>
          ) : (
            /* Still open, said plainly and without apology on either side. The
               team can see this, which is the whole point of pressing it. */
            <p className="supportverdict__done" aria-live="polite">
              {t.saidNotAnswered}{' '}
              <button
                type="button"
                className="btn btn--quiet btn--sm"
                disabled={busy}
                onClick={() => onVerdict(true)}
              >
                {t.answeredNow}
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * One support picture, signed on demand (private bucket, one hour — photos.ts).
 * A link to the full-size signed URL, because a screenshot is read at its own
 * size, not at a thumbnail's. An effect, so the suite sees only the frame; the
 * box keeps its shape from the stored size while the URL arrives.
 */
function SupportPictureLink({ path, width, height }: { path: string; width: number; height: number }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    signedSupportPictureUrl(path).then((u) => live && setUrl(u))
    return () => {
      live = false
    }
  }, [path])
  if (!url) return <span className="supportq__pic" style={{ aspectRatio: `${width} / ${height}` }} />
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="" width={width} height={height} />
    </a>
  )
}

/**
 * One reply. 118: the two sides are told apart by PLACEMENT and a soft fill
 * (the asker's replies sit left, the lane's right), and the name stays on
 * both — private to a named audience, not anonymous. 123: when the body is
 * One's rendering, the teal line sits above it and Show original swaps in the
 * author's words; its own component because each press is its own state.
 */
function SupportReply({ reply, fromAsker }: { reply: WikiComment; fromAsker: boolean }) {
  const t = SUPPORT[useHubLang()]
  const orig = useOriginal(reply.translationStatus === 'machine', () => loadCommentOriginal(reply.commentId))
  const body = orig.showingOriginal && orig.original ? orig.original.body : reply.body
  return (
    <div className={`supportq__reply${fromAsker ? '' : ' supportq__reply--lane'}`}>
      <p className="supportq__replywho">{reply.authorName ?? t.somebody}</p>
      <TranslatedLine state={orig} />
      <p className="supportq__replybody">{body ?? t.withdrawn}</p>
    </div>
  )
}

/**
 * "Chrome 154 · Windows" out of a full user-agent string (29 Sep). The whole
 * string was printed as one unbreakable chip, ~600px of it, and that chip is
 * what pushed the thread under the staff column on the desk. The full string
 * stays in the chip's title, so nothing a developer needs is lost.
 */
export function shortBrowser(ua: string): string {
  const browser =
    /Edg\/(\d+)/.exec(ua)?.[1] ? `Edge ${/Edg\/(\d+)/.exec(ua)![1]}`
    : /Firefox\/(\d+)/.exec(ua) ? `Firefox ${/Firefox\/(\d+)/.exec(ua)![1]}`
    : /Chrome\/(\d+)/.exec(ua) ? `Chrome ${/Chrome\/(\d+)/.exec(ua)![1]}`
    : /Version\/(\d+).*Safari/.exec(ua) ? `Safari ${/Version\/(\d+).*Safari/.exec(ua)![1]}`
    : null
  const os =
    /iPhone|iPad/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : null
  if (!browser && !os) return ua.length > 32 ? `${ua.slice(0, 31)}…` : ua
  return [browser, os].filter(Boolean).join(' · ')
}
